import React, { useContext, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { ApplicationContext } from "./context/GlobalState";
import { extractLocaleFromPath } from "./i18n";
import Routes from "./routes/Routes";
import Header from "./components/Header/Header";
import Footer from "./components/Footer/Footer";
import "./styles/glassmorphism.css";
import "./App.css";

function App() {
  const { state, dispatch } = useContext(ApplicationContext);
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);

  // Inject brand colors as CSS custom properties
  useEffect(() => {
    const colors = state.brand?.colors;
    if (!colors) return;
    const root = document.documentElement;
    root.style.setProperty('--color-primary', colors.primary);
    root.style.setProperty('--color-primary-light', colors.primaryLight);
    root.style.setProperty('--color-accent', colors.accent);
    root.style.setProperty('--color-accent-light', colors.accentLight);
    root.style.setProperty('--color-bg', colors.background);
    root.style.setProperty('--color-bg-alt', colors.backgroundAlt);
    root.style.setProperty('--color-text', colors.text);
    root.style.setProperty('--color-text-secondary', colors.textSecondary);
    root.style.setProperty('--color-border', colors.border);
    root.style.setProperty('--color-success', colors.success);
    root.style.setProperty('--color-warning', colors.warning);
    // Footer color
    if (colors.footer) {
      root.style.setProperty('--color-footer', colors.footer);
    }
    // RGB values for glassmorphism
    if (colors.primaryRgb) {
      root.style.setProperty('--color-primary-rgb', colors.primaryRgb);
    }
    if (colors.accentRgb) {
      root.style.setProperty('--color-accent-rgb', colors.accentRgb);
    }
    // Glass vars
    const glass = state.brand?.glass;
    if (glass) {
      root.style.setProperty('--glass-primary-rgb', glass.primaryRgb);
      root.style.setProperty('--glass-accent-rgb', glass.accentRgb);
      root.style.setProperty('--glass-hero-overlay', glass.heroOverlay);
      root.style.setProperty('--glass-nav-blur', glass.navBlur);
      root.style.setProperty('--glass-card-blur', glass.cardBlur);
      // Hero CTA button colors (contrasting against hero overlay)
      if (glass.heroCta) {
        root.style.setProperty('--hero-cta-bg', glass.heroCta.bg);
        root.style.setProperty('--hero-cta-text', glass.heroCta.text);
        root.style.setProperty('--hero-cta-hover-bg', glass.heroCta.hoverBg);
        root.style.setProperty('--hero-cta-hover-text', glass.heroCta.hoverText || glass.heroCta.text);
      } else {
        root.style.setProperty('--hero-cta-bg', 'var(--color-accent)');
        root.style.setProperty('--hero-cta-text', '#ffffff');
        root.style.setProperty('--hero-cta-hover-bg', '#c53030');
        root.style.setProperty('--hero-cta-hover-text', '#ffffff');
      }
    }
  }, [state.brand]);

  // Sync locale from URL into global state on every navigation
  useEffect(() => {
    const { locale: urlLocale } = extractLocaleFromPath(location.pathname);
    if (urlLocale !== state.locale) {
      dispatch({ type: "SET_LOCALE", payload: urlLocale });
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close mobile menu on route change only (not on menu state change)
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      prevPathRef.current = location.pathname;
      dispatch({ type: "SET_MOBILE_MENU", payload: false });
      window.scrollTo(0, 0);
    }
  }, [location.pathname, dispatch]);

  // Body scroll lock when mobile menu is open
  useEffect(() => {
    if (state.isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [state.isMobileMenuOpen]);

  // Responsive breakpoint detection
  useEffect(() => {
    const checkMobile = () => {
      const isMobile = window.innerWidth <= 1024;
      dispatch({ type: "SET_IS_MOBILE", payload: isMobile });
      if (window.innerWidth > 1024) {
        dispatch({ type: "SET_MOBILE_MENU", payload: false });
      }
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, [dispatch]);

  return (
    <div className="app">
      <Header />
      <main className="main-content">
        <Routes />
      </main>
      <Footer />
    </div>
  );
}

export default App;
